import CryptoJS from 'crypto-js';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Field, FieldArray, Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as SourceUtils from '../availableSources/SourceCardUtil';
import * as TargetUtils from '../targets/TargetCardUtil';
import * as ConnectorUtils from '../availableConnectorsV2/ConnectorCardUtil';
import * as LinkHelper from '../../utils/LinkHelper';
import * as UIAction from '../../redux/actions/ui.actions';
import * as Selectors from '../../redux/selectors';
import IOSSwitch from '../../shared/IOSSwitch';
import { Add, Remove, VisibilityOffOutlined, VisibilityOutlined, ContentCopy } from '@mui/icons-material';
import { Alert, clearModal } from '../popupAlerts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const EntityParams = (props) => {
  const {
    widgetTenant,
    connectParams: connectParamsOverride,
    flowId,
    formEl,
    linkedSource,
    linkedTarget,
    linkedConnector,
    entity,
    setLoading,
    height,
    isConnected,
    multipleTenantLinks,
    listener,
    px,
    formStyles = {},
  } = props;
  const { entityType, connect_ui_params } = entity;
  const [showPassword, setShowPassword] = useState(false);
  const [params, setParams] = useState(connect_ui_params);
  useEffect(() => {
    if (
      connectParamsOverride[entity?.id] ||
      connectParamsOverride[entity?.tap] ||
      connectParamsOverride[entity?.target]
    ) {
      if (entityType === 'source') setParams(connectParamsOverride[entity.tap]);
      if (entityType === 'target') setParams(connectParamsOverride[entity.target]);
      if (entityType === 'connector') setParams(connectParamsOverride[entity.id]);
    } else {
      setParams(connect_ui_params);
    }
  }, [entity, connectParamsOverride, connect_ui_params]);

  // Clear alerts when component unmounts or entity changes
  useEffect(() => {
    return () => {
      clearModal();
    };
  }, [entity?.id, entity?.tap, entity?.target]);

  const v_params = useMemo(
    () =>
      params &&
      Object.entries(params).filter(([field, value]) => {
        const { flow_id } = value;

        if (flow_id && flow_id !== flowId) return false;

        return true;
      }),
    [params, flowId]
  );
  const fields = useMemo(() => v_params && v_params.map(([field, value]) => field), [v_params]);

  const handleMultiselectType = useCallback((params, field, linkedEntity) => {
    const filteredValues = params[field].options.filter((x) => linkedEntity.config[field]?.includes(x.value));
    return filteredValues;
  }, []);

  const getDefaults = useCallback(() => {
    const result = {};
    if (entityType === 'source') {
      for (let field of fields) {
        if (
          linkedSource &&
          linkedSource.config &&
          linkedSource.config[field] !== undefined &&
          linkedSource.tenant_id === entity.tenant_id
        ) {
          result[field] =
            params[field]?.type === 'multiselect'
              ? handleMultiselectType(params, field, linkedSource)
              : multipleTenantLinks && entity?.config?.[field]
              ? entity?.config?.[field]
              : linkedSource.config[field];
        } else {
          const defaultValue = params[field]?.default_value;
          result[field] =
            entity[field] ||
            (defaultValue !== undefined
              ? defaultValue
              : params[field]?.type && params[field]?.type == 'boolean'
              ? false
              : params[field]?.type && params[field]?.type === 'multiselect'
              ? []
              : '');
        }
      }
    } else if (entityType === 'target') {
      for (let field of fields) {
        if (
          linkedTarget &&
          linkedTarget.config &&
          linkedTarget.config[field] !== undefined &&
          linkedTarget.tenant_id === entity.tenant_id
        )
          result[field] =
            params[field]?.type === 'multiselect'
              ? handleMultiselectType(params, field, linkedTarget)
              : multipleTenantLinks && entity?.config?.[field]
              ? entity?.config?.[field]
              : linkedTarget.config[field];
        else {
          const defaultValue = params[field]?.default_value;
          result[field] =
            entity[field] ||
            (defaultValue !== undefined
              ? defaultValue
              : params[field]?.type && params[field]?.type == 'boolean'
              ? false
              : params[field]?.type && params[field]?.type === 'multiselect'
              ? []
              : '');
        }
      }
    } else if (entityType === 'connector') {
      for (let field of fields) {
        if (
          linkedConnector &&
          linkedConnector.config &&
          linkedConnector.config[field] !== undefined &&
          linkedConnector.tenant_id === entity.tenant_id
        )
          result[field] =
            params[field]?.type === 'multiselect'
              ? handleMultiselectType(params, field, linkedConnector)
              : multipleTenantLinks && entity?.config?.[field]
              ? entity?.config?.[field]
              : linkedConnector.config[field];
        else {
          const defaultValue = params[field]?.default_value;
          result[field] =
            entity[field] ||
            (defaultValue !== undefined
              ? defaultValue
              : params[field]?.type && params[field]?.type == 'boolean'
              ? false
              : params[field]?.type && params[field]?.type === 'multiselect'
              ? []
              : '');
        }
      }
    }
    return result;
  }, [entity, entityType, linkedSource, linkedTarget, linkedConnector, fields, params]);

  const doValidate = async (values, props) => {
    clearModal();
    
    const credentialsFormValidator = Selectors.getCredentialsFormValidator();
    let customValidationErrorMessage = undefined;
    let customValidationSuccess = true;

    if (credentialsFormValidator) {
      try {
        const { errorMessage, success } =
          await credentialsFormValidator(entity.config, values, entity, flowId, entity.tenant_id ?? widgetTenant);
        customValidationSuccess = success;

        if (typeof customValidationSuccess !== 'boolean') {
          throw new Error('success must be a boolean value');
        }

        if (customValidationSuccess === true) {
          // When success is true, errorMessage must be undefined.
          if (typeof errorMessage !== 'undefined') {
            throw new Error('When success is true, errorMessage must be undefined');
          }
          customValidationErrorMessage = undefined;
        } else {
          // When success is false, errorMessage must be defined and a non-empty string
          if (typeof errorMessage !== 'string' || errorMessage.trim() === '') {
            throw new Error('When success is false, errorMessage must be a non-empty string');
          }
          customValidationErrorMessage = errorMessage;
        }
      } catch (error) {
        console.error('Error during credentialsFormValidator execution:', error.message.replace('Error: ', ''));
        customValidationErrorMessage = 'An error occurred while validating credentials';
        customValidationSuccess = false;
      }
    }
    
    return new Promise((resolve) => {
    const error = {};
    let validated = true;
    let jsonErrorField = null;
    
    (v_params || []).forEach(([field, param]) => {
      const { required = true } = param;
      const value = values[field];

      if (required && (value === undefined || value === '')) {
        error[field] = 'This field is required';
        validated = false;
      }

      try{
        if (param.type === 'json' && value) {
          const parsed = JSON.parse(value);
          // Only allow objects, not primitives like strings, numbers, booleans
          if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            throw new Error('JSON must be an object');
          }
        }
      } catch (err) {
        const errorMessage = err.message === 'JSON must be an object' 
          ? 'JSON must be an object (not an array or primitive value)'
          : 'Invalid JSON format in this field. Please check your syntax.';
        error[field] = errorMessage;
        if (!jsonErrorField) {
          jsonErrorField = field;
        }
        validated = false;
      }
    });

    if (!validated || !customValidationSuccess) {
      if (jsonErrorField) {
        const fieldParam = v_params?.find(([field]) => field === jsonErrorField)?.[1];
        const fieldLabel = fieldParam?.label || jsonErrorField;
        Alert({ 
          text: `Invalid JSON format detected in ${fieldLabel}. Please ensure your input is valid JSON.`, 
          type: 'error',
          duration: null
        });
      } else if (customValidationErrorMessage) {
        Alert({ 
          text: customValidationErrorMessage, 
          type: 'error'
        });
        error['customValidationErrorMessage'] = true
      } else {
        Alert({ 
          text: `Required field${Object.keys(error).length > 1 ? 's' : ''} missing!`, 
          type: 'error'
        });
      }
    }
    
    // Return errors object directly (not in a promise)
    resolve(error);
  });
  };

  const onDone = async (entity, values) => {
    setLoading(true);

    //trim all strings
    Object.entries(values.config).forEach(([key, value]) => {
      if (typeof values.config[key] === 'string') values.config[key] = value.trim();
    });
    
    // Process date fields to ensure they're set to midnight
    Object.entries(params || {}).forEach(([field, fieldProps]) => {
      if (fieldProps.type === 'date' && values.config[field]) {
        // Parse the date and format with UTC midnight time
        const dateValue = values.config[field];
        
        // If it's already in ISO format with time, just ensure it's midnight
        // If it's in YYYY-MM-DD format, append midnight time
        if (dateValue.includes('T')) {
          // Already has time, parse and set to midnight
          const parsed = dayjs(dateValue);
          if (parsed.isValid()) {
            values.config[field] = parsed.format('YYYY-MM-DD') + 'T00:00:00Z';
          }
        } else {
          // Just date, append midnight UTC time
          values.config[field] = dateValue + 'T00:00:00Z';
        }
        
        console.log(`Processed date field ${field}:`, values.config[field]);
      }
    });
    
    try {
      if (entityType === 'connector') {
        await ConnectorUtils.handleDirectLinkConnector(entity, values);
      } else if (entityType === 'target') {
        await TargetUtils.handleDirectLinkTarget(entity, values);
      } else {
        await SourceUtils.handleDirectLinkSource(entity, values);
      }
      // Refetch the flows
      await LinkHelper.postLink(entity.tenant_id, entityType, flowId);
      if (entity.type !== 'oauth') Alert({ text: `Successfully linked to ${entity.label}`, type: 'success' });

      // update entity
      const newEntity = {...entity, config: values, entityType}

      if (!isConnected && entity.show_trigger_url) {
        // console.log("Redirecting to triggers tab");
        // If first connection and entity has show_trigger_url flag, go to triggers tab
        UIAction.goToEntityConfig(newEntity, entityType, undefined, false, true);
      } else if (
        !isConnected &&
        entityType === 'source' &&
        (entity.tap === 'sftp-files' || (['google-sheets-beta', 'google-drive'].includes(entity.tap) && !entity.connect_ui_params))
      ) {
        // If specific source types, go to entity config
        UIAction.goToEntityConfig(newEntity);
      } else {
        // Otherwise follow the normal flow
        UIAction.goNextStepOrBack(newEntity);
      }
    } catch (err) {
      const msg = err.message.replace('BadRequestError:', '').trim();

      if(entityType === 'source' && listener && listener.onSourceLinkFailed)
        listener.onSourceLinkFailed(entity.tap, flowId, entity.tenant_id ?? widgetTenant, msg)


      if(entityType === 'connector' && listener && listener.onConnectorLinkFailed)
        listener.onConnectorLinkFailed(entity.id, flowId, entity.tenant_id ?? widgetTenant, msg)

      if(entityType === 'target' && listener && listener.onTargetLinkFailed)
        listener.onTargetLinkFailed(entity.target, flowId, entity.tenant_id ?? widgetTenant, msg)

      Alert({ text: `Failed to link to ${entity.label}: ${msg}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const createFields = useCallback(
    (v_params, values, fieldPrefix = '', setFieldValue) => {
      return v_params?.map(([id, fieldProps]) => {
        let { label, description, adornment = {}, type = 'text' } = fieldProps;
        const fieldId = `${fieldPrefix}${id}`;

        if (type == 'boolean') {
          return (
            <Box display="flex" key={label} pt={1} pb={0.5} borderBottom="1px solid common.divider">
              <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5} sx={{ flex: 1 }}>
                <InputLabel sx={{ width: '100%', cursor: 'pointer' }} htmlFor={fieldId}>
                  {label}
                </InputLabel>
                {description && (
                  <Typography 
                    variant="caption" 
                    color="text.light" 
                    sx={{ fontSize: '0.75rem', fontWeight: 300 }}
                  >
                    {description}
                  </Typography>
                )}
              </Box>
              <Field type="checkbox" name={fieldId}>
                {({ field, form, meta }) => <IOSSwitch {...field} id={fieldId} color="primary" />}
              </Field>
            </Box>
          );
        }

        if (type == 'select') {
          const { options = [] } = fieldProps;

          return (
            <Field name={fieldId} key={fieldId}>
              {({ field, form, meta }) => {
                const selectedOption = options.find((opt) => opt.value === field.value) || null;
                
                return (
                  <Autocomplete
                    options={options}
                    value={selectedOption}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    ListboxProps={{
                      style: {
                        maxHeight: '250px',
                        overflow: 'auto',
                      },
                    }}
                    PaperComponent={({ children, ...other }) => (
                      <Paper
                        {...other}
                        sx={{
                          maxHeight: '250px',
                          overflow: 'hidden',
                          p: 0,
                          '& .hotglue-w-MuiAutocomplete-listbox': {
                            maxHeight: '250px',
                            overflow: 'auto',
                            m: 0,
                            '& .hotglue-w-MuiAutocomplete-option': {
                              fontSize: '16px',
                              fontWeight: 300,
                              px: 2,
                              py: '6px',
                            },
                            '& .hotglue-w-MuiAutocomplete-option:hover': {
                              backgroundColor: '#FAFAFA',
                            },
                            "& .hotglue-w-MuiAutocomplete-option[aria-selected='true']": {
                              bgcolor: (theme) => theme.palette.primary.light,
                              color: (theme) => theme.palette.primary.contrastText,
                            },
                            "& .hotglue-w-MuiAutocomplete-option[aria-selected='false']": {
                              fontWeight: 200,
                            },
                          },
                        }}
                      >
                        {children}
                      </Paper>
                    )}
                    sx={{
                      '& .hotglue-w-MuiAutocomplete-endAdornment': {
                        right: 8,
                      },
                    }}
                    onChange={(e, newValue) => {
                      setFieldValue(fieldId, newValue ? newValue.value : '');
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={label} error={meta.error} sx={{ width: '100%', mb: 2 }} />
                    )}
                  />
                );
              }}
            </Field>
          );
        }

        if (type === 'multiselect') {
          const { options = [] } = fieldProps;
          return (
            <Autocomplete
              id={fieldId}
              name={fieldId}
              key={fieldId}
              options={options}
              defaultValue={values[fieldId]}
              getOptionLabel={(option) => option.name}
              multiple
              PaperComponent={CustomPaper}
              sx={{
                '& .hotglue-w-MuiInputBase-root': {
                  gap: '4px',
                },
                '& .hotglue-w-MuiAutocomplete-endAdornment': {
                  right: '5px',
                },
              }}
              onChange={(e, marketplaces) => {
                setFieldValue(
                  fieldId,
                  marketplaces.map((marketplace) => marketplace.value)
                );
              }}
              renderTags={(tagValue, getTagProps) => {
                return tagValue.map((option, index) => (
                  <Chip
                    key={index}
                    sx={{
                      '& svg': {
                        fontSize: '16px',
                        mr: '4px',
                      },
                      '& span': {
                        fontSize: '12px',
                        px: '4px',
                      },
                    }}
                    color="text"
                    {...getTagProps({ index })}
                    label={option.name}
                    size="small"
                    variant="outlined"
                  />
                ));
              }}
              renderInput={(params) => (
                <Field component={TextField} {...params} name={fieldId} label={label} fullWidth />
              )}
            />
          );
        }

        if (type == 'list') {
          const { params } = fieldProps;
          const startingVal = {};
          Object.keys(params).forEach((p) => (startingVal[p] = ''));

          return (
            <FieldArray
              key={fieldId}
              name={fieldId}
              render={(arrayHelpers) => (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={1}>
                    <Typography
                      variant="caption"
                      letterSpacing="1px"
                      ml={1}
                      textTransform="uppercase"
                      color="text.light"
                      fontWeight="400"
                      fontSize="12px"
                    >
                      List of {label}
                    </Typography>
                    <Button
                      variant="filledTonal"
                      color="secondary"
                      onClick={() => arrayHelpers.insert(0, Object.assign({}, startingVal))}
                    >
                      <Add sx={{ fontSize: '16px' }} />
                      <Typography variant="body2" ml={1} fontWeight="400">Add {label}</Typography>
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 4 }} />
                  {values[fieldId] &&
                    values?.[fieldId]?.slice(0)?.reverse()?.map((friend, index) => (
                      <div key={index}>
                        {createFields(Object.entries(params), values, `${fieldId}.${index}.`, setFieldValue)}
                        <Box display="flex" justifyContent="flex-end" mb={4}>
                          <Button color="primary" variant="text" onClick={() => arrayHelpers.remove(index)}>
                            <Remove sx={{ fontSize: '16px' }} />
                            <Typography variant="body2" ml={1} fontWeight="400">Remove {label}</Typography>
                          </Button>
                        </Box>
                      </div>
                    ))}
                </Box>
              )}
            />
          );
        }

        if (type === 'date') {
          return (
            <Field type="text" name={fieldId} key={fieldId}>
              {({ field, form, meta }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    {...field}
                    label={label}
                    format={fieldProps.format || "YYYY-MM-DD"}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        error: meta.error,
                        helperText: description,
                        sx: { width: '100%', mb: 2 },
                        InputLabelProps: {
                          sx: { fontWeight: 300 },
                          shrink: true
                        },
                        FormHelperTextProps: {
                          sx: {
                            fontSize: '12px',
                            fontWeight: '300',
                            color: (theme) => theme.palette.text.light,
                          }
                        },
                        placeholder: fieldProps.format || "YYYY-MM-DD",
                        onClick: (e) => {
                          e.currentTarget.click();
                        }
                      },
                      openPickerButton: {
                        sx: {
                          mr: -1,
                          padding: '5px',
                          border: 'none',
                          '&:hover': {
                            backgroundColor: 'transparent'
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '20px'
                          }
                        }
                      }
                    }}
                    onChange={(newValue) => {
                      if (!newValue) {
                        setFieldValue(fieldId, null);
                        return;
                      }
                      
                      try {
                        // Store just the date in YYYY-MM-DD format
                        // This prevents any timezone shifting during storage
                        const dateString = dayjs(newValue).isValid()
                          ? dayjs(newValue).format('YYYY-MM-DD')
                          : null;
                        
                        console.log('Saving date:', {
                          original: newValue.format(),
                          formatted: dateString
                        });
                        
                        setFieldValue(fieldId, dateString);
                      } catch (error) {
                        console.error('Date formatting error:', error);
                        setFieldValue(fieldId, null);
                      }
                    }}
                    value={values[fieldId] ? dayjs(values[fieldId].split('T')[0]) : null}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& .MuiInputAdornment-root': {
                          '& .MuiButtonBase-root': {
                            '&:hover': {
                              bgcolor: 'transparent'
                            }
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              )}
            </Field>
          );
        }

        const inputProps = {};

        // Generate adornments
        if (type == 'password') {
          inputProps['endAdornment'] = (
            <InputAdornment position="end">
              <IconButton
                color="default"
                tabIndex={-1}
                edge="end"
                size="small"
                aria-label="toggle password visibility"
                onClick={() => {
                  setShowPassword(!showPassword);
                }}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
              >
                {showPassword ? (
                  <VisibilityOutlined sx={{ fontSize: '16px' }} />
                ) : (
                  <VisibilityOffOutlined sx={{ fontSize: '16px' }} />
                )}
              </IconButton>
            </InputAdornment>
          );
        } else {
          if (adornment.start)
            inputProps['startAdornment'] = <InputAdornment position="start">{adornment.start}</InputAdornment>;

          if (adornment.end && adornment.end.length > 17) {
            inputProps['endAdornment'] = (
              <InputAdornment position="end">
                <Tooltip title={adornment.end}>
                  <Typography color="text.primary">{`${adornment.end.slice(0, 17)}...`}</Typography>
                </Tooltip>
              </InputAdornment>
            );
          } else {
            inputProps['endAdornment'] = <InputAdornment position="end">{adornment.end}</InputAdornment>;
          }
        }

        let extraProps = {}

        // TODO: We could limit this to only s3 target
        if (fieldId === "aws_external_id") {
          // Generate a unique value for the external id based on tenant id and env id
          const envId = Selectors.getEnvId();
          const combined = `${envId}:${widgetTenant}`;
          // Create a SHA-256 hash of the combined string
          const externalId = CryptoJS.SHA256(combined).toString(CryptoJS.enc.Hex).slice(0, 28);
          // Set the value
          values[fieldId] = externalId;

          const handleCopy = () => {
            navigator.clipboard.writeText(externalId);
          };

          inputProps['endAdornment'] = <InputAdornment position="end">
             <IconButton onClick={handleCopy}>
                <ContentCopy />
              </IconButton>
          </InputAdornment>;

          extraProps = {
            disabled: true,
            value: externalId
          }
        }

        return (
          <Field type="text" name={fieldId} key={fieldId}>
            {({ field, form, meta }) => (
              <TextField
                {...field}
                {...extraProps}
                onChange={(e) =>
                  setFieldValue(
                    fieldId,
                    type === 'multiline' ? e.target.value.replaceAll(/\r?\n/g, '\n') :
                    type === 'number' ? parseFloat(e.target.value) : e.target.value
                  )
                }
                variant="outlined"
                sx={{ width: '100%', mb: 2 }}
                error={!!meta.error}
                label={label}
                helperText={meta.error || description}
                multiline={type === 'multiline' || type === 'json'}
                type={showPassword ? 'text' : type}
                InputProps={inputProps}
                InputLabelProps={{
                  sx: { fontWeight: 300 },
                }}
                FormHelperTextProps={{
                  sx: {
                    fontSize: '12px',
                    fontWeight: '300',
                    color: (theme) => meta.error ? theme.palette.error.main : theme.palette.text.light,
                  },
                }}
                inputProps={{
                  autoComplete: 'new-password',
                }}
                autoComplete="off"
              />
            )}
          </Field>
        );
      });
    },
    [v_params, showPassword]
  );

  return (
    <Formik
      innerRef={formEl}
      initialValues={getDefaults()}
      validateOnChange={false}
      validateOnBlur={false}
      validate={doValidate}
      onSubmit={(values, { setSubmitting }) => {
        // Submit to link source
        onDone(entity, { config: values });
        // Mark submitting as complete
        setSubmitting(false);
      }}
    >
      {({ values, setFieldValue }) => (
        <Box
          component={Form}
          my={0}
          id="app-params-form"
          display="flex"
          flexDirection="column"
          height={height || 400}
          overflow="hidden"
          sx={{
            overflow: 'hidden',
            overflowY: 'auto',
            ...formStyles,
          }}
        >
          <Box display="flex" flexDirection="column" px={px || 2} gap={1}>
            {createFields(v_params, values, undefined, setFieldValue)}
          </Box>
        </Box>
      )}
    </Formik>
  );
};

function CustomPaper({ children }) {
  return (
    <Paper
      sx={{
        p: 0,
        '& .hotglue-w-MuiAutocomplete-listbox': {
          m: 0,
          '& .hotglue-w-MuiAutocomplete-option': {
            fontFamily: 'CircularStd',
            fontSize: '16px',
            fontWeight: 400,
            px: 2,
            py: '6px',
          },
          '& .hotglue-w-MuiAutocomplete-option:hover': {
            backgroundColor: '#FAFAFA',
          },
          "& .hotglue-w-MuiAutocomplete-option[aria-selected='true']": {
            bgcolor: (theme) => theme.palette.primary.light,
            color: (theme) => theme.palette.primary.contrastText,
          },
          "& .hotglue-w-MuiAutocomplete-option[aria-selected='false']": {
            fontWeight: 200,
          },
        },
      }}
    >
      {children}
    </Paper>
  );
}

export default EntityParams;
